// THE NETTI WALKTHROUGH — every walk netti takes a person on, in one registry
// (no React, no I/O: the server, the shell, the conductor and the self-check all
// read the same list).
//
// A walk is a short script of beats. Each beat is one line netti says in the
// guide (components/onboarding/NettiGuide), and may first OPEN a place (`goto`,
// plus a view inside it), POINT at something on the page (`target`, a
// lib/walkthrough/targets key, drawn by components/walkthrough/Pointer), or show
// the you space's example (`ghost`, never written anywhere). Four kinds:
//
//   first-run  the walk after the first connection is kept (the conductor plays
//              it as the `walk` stage of lib/os/onboarding); ends in "enter"
//   update     greets once, the first time someone is here after it shipped,
//              if they can see the feature it is about (a `rev` bump greets
//              everyone again whose stored rev is older)
//   space      plays once, the first time you stand in a place it is about
//   guide      only when asked (a "walk me through it" link)
//
// netti's tours (lib/tours) are the update/space/guide walks: they are adapted
// in here, so there is ONE registry, ONE seen store (user_settings.tours, read
// rev-aware by engine.ts, no migration) and ONE self-check (selfcheck.test.ts),
// which fails CI when a beat runs long, a place stops resolving, an anchor
// leaves its component, or a feature ships with no walk decision (features.ts).
//
// The house rules every beat keeps (and the self-check enforces): at most five
// beats a walk (the first-run walk eight, FIRST_RUN_MAX_BEATS), at most forty
// words a line, lowercase, no em dash.

import { TOURS, type Tour, type TourBeat } from "../tours/tours";
import type { TargetKey } from "./targets";

export type WalkKind = "first-run" | "update" | "space" | "guide";

export interface WalkAct {
  label: string;
  /** what the chip does: next · later · done · enter · make-agent (the player interprets it) */
  act: string;
}

export interface Beat {
  /** stable within its walk */
  id: string;
  /** netti's line. `{name}` is the person's name, `{agent}` the agent being shown */
  say: string;
  /** a place to open before the line is spoken (a tree path; `{agent}` is filled by the player) */
  goto?: string;
  /** a view to open inside that place: the shell's `innernet:panel-view` bridge */
  view?: { panel: string; view: string };
  /** a place on the page netti points at while the beat is up */
  target?: TargetKey;
  /** show the you space's example while this beat is up (lib/you/walkGhost): render-only, never stored */
  ghost?: boolean;
  /** the chips under the line (default: next, and done on the last beat) */
  acts?: WalkAct[];
  /** the beat only plays when this holds; otherwise it is stepped over */
  needs?: "agent" | "no-agent";
  /** said instead to someone who cannot see a connection's read and write
   *  controls (the private ring, F.privateRing): every line is true for its reader */
  sayWithoutRing?: string;
  /** opens a window once the place is open: `connection` = the one just kept (lib/connect/openConnection) */
  opens?: "connection";
  /** the subject that window opens on (default: its settings) */
  section?: "instructions";
  /** a prompt the beat hands over, shown under the line with its own copy
   *  (lib/onboarding/prompts: project = PROMPT_PROJECT, skills = PROMPT_SKILLS,
   *  instruction = PROMPT_SYNC, the standing instruction) */
  prompt?: "project" | "skills" | "instruction";
  /** netti's line once that prompt was copied (it is listening for what arrives) */
  copied?: string;
  /** netti's line once it arrived: `{project}` is the new folder's name, `{skills}` how many came */
  arrived?: string;
}

export interface Walk {
  id: string;
  kind: WalkKind;
  /** the walk's revision. Bump it when an update walk should greet everyone again */
  rev: number;
  beats: Beat[];
  /** a feature id (lib/features/registry F): the walk exists only for someone who can see it */
  feature?: string;
  /** when it shipped (ISO date) */
  since?: string;
  /** a space walk plays the first time the path matches */
  at?: RegExp;
  /** seeing this walk counts as having seen these too */
  covers?: string[];
}

// ── the first-run walk (2026-10-22, rev 2 the same day) ──────────────────────
// Right after the first connection is kept, in John's order: the connection
// works, and here is what you control of it (its own window, opened on its
// settings); then its standing instruction, so every new chat reaches for the
// memory on its own (`{where}` is that tool's own place for it; 2026-10-24);
// then MAKE IT YOURS: bring the project you are in, then your skills
// (each a prompt to copy into that ai, each noticed the moment it lands, each
// skippable because the ai may have done it in the chat already); then what
// innernet gives back: the daily digest (on by default, 8:00 am local, an "i"
// showing an example), the window an ai opens under its answer, the you space
// with an example of how it fills in, and the activity river, where the door
// is. The conductor deep-links each place, opens the kept connection's window,
// hands each prompt over in the guide, turns the digest on once
// (lib/digest/prefsClient ensureDigestOn) and holds the example only while the
// you beat is up. The first-run walk may say up to eight things: each of its
// beats is a thing the person does or gets, not a stop on a tour.

export const FIRST_RUN_WALK: Walk = {
  id: "first-run",
  kind: "first-run",
  rev: 2,
  since: "2026-10-22",
  beats: [
    {
      id: "walk.connection",
      goto: "/connections",
      view: { panel: "connections", view: "tools" },
      opens: "connection",
      target: "conn-controls",
      say: "{tool} is connected and working. this is its own window: here you choose how much of you it may read, whether it writes freely or asks first, and you can let it go any time.",
      sayWithoutRing: "{tool} is connected and working. this is its own window: when it last called, what you call it, and letting it go are all here, whenever you want them.",
      acts: [{ label: "next", act: "next" }],
    },
    {
      id: "walk.instructions",
      opens: "connection",
      section: "instructions",
      prompt: "instruction",
      say: "one more thing for {tool}: a standing instruction, so every new chat reaches for your memory on its own. copy this into {where}, once.",
      copied: "paste it into {where} and save. from the next chat on, {tool} starts with you.",
      // nothing to hear arrive: once copied, the conductor offers next
      acts: [{ label: "skip for now", act: "skip" }],
    },
    {
      id: "walk.project",
      goto: "/projects",
      prompt: "project",
      say: "now bring the project you're working on. copy this into {tool} and it saves it here as a folder, so later chats load a light, ready context instead of rereading everything.",
      copied: "paste it into {tool}. i'll know the moment the folder arrives.",
      arrived: "{project} is here, kept as a folder. open it whenever you like, it's yours.",
      acts: [{ label: "skip for now", act: "skip" }],
    },
    {
      id: "walk.skills",
      goto: "/skills",
      prompt: "skills",
      say: "and your skills. copy this into {tool} and it brings each one in, kept private, so every ai you connect can use them.",
      copied: "paste it into {tool}. i'll know when they arrive.",
      arrived: "{skills} came in. they live here, ready for every ai you connect.",
      acts: [{ label: "skip for now", act: "skip" }],
    },
    {
      id: "walk.digest",
      goto: "/settings",
      view: { panel: "settings", view: "digest" },
      target: "digest-time",
      say: "every night netti reads your day and writes it back as a short letter. it's on, and it lands at 8:00 am your time. change the time if you like, or press i to see one.",
      acts: [{ label: "next", act: "next" }],
    },
    {
      id: "walk.widget",
      goto: "/settings",
      view: { panel: "settings", view: "chats" },
      target: "widget-preview",
      say: "in chats that can show it, your ai opens this small window under its answer. you choose what it shows, right here.",
      acts: [{ label: "next", act: "next" }],
    },
    {
      id: "walk.you",
      goto: "/you",
      target: "you-summary",
      ghost: true,
      say: "this is you. it's quiet today, so here's an example of how it fills in as your ais remember things. yours starts with the next thing you tell one.",
      acts: [{ label: "next", act: "next" }],
    },
    {
      id: "walk.done",
      goto: "/activity",
      target: "activity-river",
      say: "and this is the river: everything your ais read and keep, as it happens. that's all you need for now. the rest you'll find as you go.",
      acts: [{ label: "enter innernet", act: "enter" }],
    },
  ],
};

/** how many beats the first-run walk may hold (every other walk: five) */
export const FIRST_RUN_MAX_BEATS = 8;

// ── netti's tours, as walks ──────────────────────────────────────────────────

function beatOf(b: TourBeat): Beat {
  return {
    id: b.id,
    say: b.text,
    ...(b.goto ? { goto: b.goto } : {}),
    ...(b.target ? { target: b.target } : {}),
    ...(b.needs ? { needs: b.needs } : {}),
    ...(b.actions?.length ? { acts: b.actions.map((a) => ({ label: a.label, act: a.act })) } : {}),
  };
}

/** a tour (lib/tours) read as a walk: the same ids, words, places and chips */
export function tourToWalk(t: Tour): Walk {
  return {
    id: t.id,
    kind: t.kind,
    rev: t.rev ?? 1,
    beats: t.beats.map(beatOf),
    ...(t.feature ? { feature: t.feature } : {}),
    since: t.since,
    ...(t.at ? { at: t.at } : {}),
    ...(t.covers ? { covers: t.covers } : {}),
  };
}

/** every walk netti can take, first-run first */
export const WALKS: Walk[] = [FIRST_RUN_WALK, ...TOURS.map(tourToWalk)];

export const WALK_BY_ID: ReadonlyMap<string, Walk> = new Map(WALKS.map((w) => [w.id, w]));

/** a beat's place (a static path), or null when it has none or it must be filled in (`{agent}`) */
export function staticGoto(b: Beat): string | null {
  return b.goto && !b.goto.includes("{") ? b.goto : null;
}
